import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import {
  looksLikeLink,
  PLACE_QUERY_MIN,
  type PlaceSuggestion,
  type Tenant,
  type VenueProfile,
} from '@kratispot/shared';
import { MapPin, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ApiError, placeFromLink, searchPlaces } from '@/lib/api';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { downsized } from '@/lib/logo-image';

// What a save does with the logo: keep it, replace it, or take it off. §3
export type LogoChange =
  { readonly kind: 'keep' | 'remove' } | { readonly kind: 'put'; readonly blob: Blob };

// Location is its own control: a search box, so it is not in this list. §3
type FieldKey = 'name' | 'slug' | 'venueType' | 'spotKind' | 'ownerDisplayName';

// Label copy per field, so adding a profile field is one row, not a new form.
const FIELDS: readonly { key: FieldKey; label: CopyKey; required: boolean }[] = [
  { key: 'name', label: 'name', required: true },
  { key: 'slug', label: 'venueSlug', required: true },
  { key: 'venueType', label: 'venueType', required: true },
  { key: 'spotKind', label: 'spotKind', required: true },
  { key: 'ownerDisplayName', label: 'ownerDisplayName', required: false },
];

// The address the card shows, and the place behind it that Phase 8 will read.
export type PlaceDraft = {
  readonly location: string;
  readonly placeId: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
};

const EMPTY: Record<FieldKey, string> = {
  name: '',
  slug: '',
  venueType: '',
  spotKind: '',
  ownerDisplayName: '',
};

export function draftOf(tenant: Tenant | null): Record<FieldKey, string> {
  if (tenant === null) return EMPTY;
  return {
    name: tenant.name,
    slug: tenant.slug,
    venueType: tenant.venueType,
    spotKind: tenant.spotKind,
    ownerDisplayName: tenant.ownerDisplayName ?? '',
  };
}

export function placeOf(tenant: Tenant | null): PlaceDraft {
  return {
    location: tenant?.location ?? '',
    placeId: tenant?.placeId ?? null,
    latitude: tenant?.latitude ?? null,
    longitude: tenant?.longitude ?? null,
  };
}

export function VenueDialog({
  tenant,
  onSubmit,
  onClose,
  error,
  busy,
}: {
  tenant: Tenant | null;
  onSubmit: (profile: VenueProfile, logo: LogoChange) => void;
  onClose: () => void;
  error: string | null;
  busy: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => draftOf(tenant));
  const [place, setPlace] = useState<PlaceDraft>(() => placeOf(tenant));
  const [suggestions, setSuggestions] = useState<readonly PlaceSuggestion[]>([]);
  const [mapProblem, setMapProblem] = useState(false);
  const [searched, setSearched] = useState(false);
  const [badLink, setBadLink] = useState(false);
  const [logo, setLogo] = useState<LogoChange>({ kind: 'keep' });
  const [preview, setPreview] = useState<string | null>(tenant?.logoUrl ?? null);
  const picker = useRef<HTMLInputElement>(null);
  const title = t(tenant === null ? 'newVenue' : 'editVenue');

  // An object URL is the browser's own handle on the picked bytes; let it go.
  useEffect(() => {
    if (logo.kind !== 'put') return;
    const url = URL.createObjectURL(logo.blob);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logo]);

  async function pick(file: File | undefined): Promise<void> {
    if (file !== undefined) setLogo({ kind: 'put', blob: await downsized(file) });
  }

  // Typing over a picked address unpicks the place: the two must agree. §3
  function typeAddress(location: string): void {
    setPlace({ location, placeId: null, latitude: null, longitude: null });
    setSearched(false);
    setBadLink(false);
  }

  // One box: a name to look up, or a Google Maps link the owner has. §3
  async function find(): Promise<void> {
    const asked = place.location.trim();
    if (asked.length < PLACE_QUERY_MIN) return;
    setMapProblem(false);
    setBadLink(false);
    setSearched(true);
    if (looksLikeLink(asked)) return followLink(asked);
    try {
      setSuggestions((await searchPlaces(asked)).places);
    } catch {
      setSuggestions([]);
      setMapProblem(true);
    }
  }

  async function followLink(url: string): Promise<void> {
    setSuggestions([]);
    try {
      const found = await placeFromLink(url);
      setSearched(false);
      setPlace({
        location: found.address,
        placeId: found.placeId,
        latitude: found.latitude,
        longitude: found.longitude,
      });
    } catch (error) {
      const refused = error instanceof ApiError && error.status === 400;
      setBadLink(refused);
      setMapProblem(!refused);
    }
  }

  function choose(suggestion: PlaceSuggestion): void {
    setSuggestions([]);
    setSearched(false);
    setPlace({
      location: suggestion.description,
      placeId: suggestion.placeId,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
    });
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit(
      {
        name: draft.name,
        slug: draft.slug,
        venueType: draft.venueType,
        spotKind: draft.spotKind,
        location: place.location,
        placeId: place.placeId,
        latitude: place.latitude,
        longitude: place.longitude,
        ownerDisplayName: draft.ownerDisplayName,
      },
      logo,
    );
  }

  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-4 pt-safe pb-safe">
      <button
        type="button"
        aria-label={t('close')}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <form
        role="dialog"
        aria-label={title}
        onSubmit={submit}
        className="relative max-h-full w-full max-w-sm overflow-auto rounded-2xl bg-card p-5 shadow-xl"
      >
        <h2 className="mb-4 text-base font-bold">{title}</h2>

        {FIELDS.map((field) => (
          <div key={field.key}>
            <label
              className="mb-1 block text-xs font-semibold text-muted-foreground"
              htmlFor={`venue-${field.key}`}
            >
              {t(field.label)}
            </label>
            <input
              id={`venue-${field.key}`}
              required={field.required}
              value={draft[field.key]}
              onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
              className="mb-3 w-full rounded-lg border px-3 py-2 text-base"
            />
          </div>
        ))}

        <label
          className="mb-1 block text-xs font-semibold text-muted-foreground"
          htmlFor="venue-location"
        >
          {t('location')}
        </label>
        <div className="mb-1 flex gap-2">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              id="venue-location"
              autoComplete="off"
              placeholder={t('locationPlaceholder')}
              value={place.location}
              onChange={(event) => typeAddress(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                // Enter searches; it must not submit the whole venue form. §3
                event.preventDefault();
                void find();
              }}
              className="w-full rounded-lg border py-2 ps-9 pe-3 text-base"
            />
          </div>
          <Button type="button" variant="outline" onClick={() => void find()}>
            {t('findPlace')}
          </Button>
        </div>
        {/* A place's own name over its address, as a map's own box reads. §3 */}
        {suggestions.length > 0 && (
          <ul
            data-testid="place-hits"
            className="mb-1 max-h-44 overflow-auto rounded-lg border bg-card text-sm"
          >
            {suggestions.map((suggestion) => (
              <li key={suggestion.placeId}>
                <button
                  type="button"
                  className="flex w-full items-start gap-2 px-3 py-2 text-start hover:bg-muted"
                  onClick={() => choose(suggestion)}
                >
                  <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0">
                    {suggestion.name !== '' && (
                      <b className="block truncate font-semibold">{suggestion.name}</b>
                    )}
                    <span className="block text-xs text-muted-foreground">
                      {suggestion.description}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {searched && suggestions.length === 0 && !mapProblem && (
          <p className="mb-1 rounded-lg border px-3 py-2 text-xs text-muted-foreground">
            {t('locationNoHits')}
          </p>
        )}
        {/* Where the venue actually stands, so a guest can be sent there. §3 */}
        {place.latitude !== null && place.longitude !== null && (
          <p className="mb-1 text-[11px] text-muted-foreground" data-testid="place-point">
            {t('locationPinned')} {place.latitude.toFixed(5)}, {place.longitude.toFixed(5)}
          </p>
        )}
        <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">
          {t(badLink ? 'locationBadLink' : mapProblem ? 'locationOffline' : 'locationSearchHint')}
        </p>

        <span className="mb-1 block text-xs font-semibold text-muted-foreground">
          {t('venueLogo')}
        </span>
        <div className="mb-3 flex items-center gap-3">
          {preview === null ? (
            <span className="grid size-11 place-items-center rounded-xl border bg-muted text-[11px] text-muted-foreground">
              —
            </span>
          ) : (
            <img
              src={preview}
              alt={t('venueLogo')}
              className="size-11 rounded-xl border object-cover"
            />
          )}
          <input
            ref={picker}
            id="venue-logo"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            aria-label={t('venueLogo')}
            className="hidden"
            onChange={(event) => void pick(event.target.files?.[0])}
          />
          <Button type="button" variant="outline" size="sm" onClick={() => picker.current?.click()}>
            {t('chooseFile')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={preview === null}
            onClick={() => {
              setLogo({ kind: 'remove' });
              setPreview(null);
            }}
          >
            {t('remove')}
          </Button>
        </div>

        {error !== null && (
          <p role="alert" className="mt-1 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {t('save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
